// Copyright The Linux Foundation and each contributor to LFX.
// SPDX-License-Identifier: MIT
import { ChartData, HealthScore } from '@lfx/key-projects/key-projects.model';

/**
 * Health Score Configuration - Centralized constants for health score system
 */
export const HEALTH_SCORE_CONFIG = {
  // Score ranges for health score levels
  RANGES: {
    EXCELLENT_MIN: 80,
    HEALTHY_MIN: 60,
    STABLE_MIN: 40,
    UNSTEADY_MIN: 20,
    CRITICAL_MIN: 0
  },

  PROGRESS_BAR_RANGES: {
    // > 75 is posisitve, 25 < x <= 75 is warning, <= 25 is negative
    POSITIVE_MIN: 75,
    WARNING_MIN: 26,
    NEGATIVE_MIN: 0
  },

  // Health score level labels
  LABELS: {
    excellent: 'Excellent',
    healthy: 'Healthy',
    stable: 'Stable',
    unsteady: 'Unsteady',
    critical: 'Critical'
  },

  // Health score colors - aligned with design system variables
  COLORS: {
    excellent: {
      primary: '#10bc8a', // --lf-color-success
      background: '#10bc8a', // --lf-color-success
      text: '#ffffff' // --lf-color-text-white
    },
    healthy: {
      primary: '#10bc8a', // --lf-color-success
      background: '#dcf7f0', // --lf-color-success-light
      text: '#10bc8a' // --lf-color-success
    },
    stable: {
      primary: '#009aff', // --lf-color-primary
      background: '#e6f3ff', // --lf-color-primary-light
      text: '#009aff' // --lf-color-primary
    },
    unsteady: {
      primary: '#f89e22', // --lf-color-warning
      background: '#fdf2e0', // --lf-color-warning-light
      text: '#f89e22' // --lf-color-warning
    },
    critical: {
      primary: '#f56565', // --lf-color-danger
      background: '#f56565', // --lf-color-danger
      text: '#ffffff' // --lf-color-text-white
    }
  },

  PROGRESS_BAR_COLORS: {
   positive: '#10bc8a',
   warning: '#f89e22',
   negative: '#f56565'
  },

  // Default colors for placeholders and empty states
  DEFAULT_COLORS: {
    placeholder: '#DBDCDE',
    background: '#f8f9fa'
  }
} as const;

/**
 * Helper functions for key projects feature
 * Contains utility functions, data transformations, and mappings
 */
export class KeyProjectsHelper {

  static mapHealthScoreToLevel(score: number): HealthScore {
    if (score >= HEALTH_SCORE_CONFIG.RANGES.EXCELLENT_MIN) {
      return {
        level: 'excellent' as const,
        color: HEALTH_SCORE_CONFIG.COLORS.excellent.text,
        backgroundColor: HEALTH_SCORE_CONFIG.COLORS.excellent.background
      };
    } else if (score >= HEALTH_SCORE_CONFIG.RANGES.HEALTHY_MIN) {
      return {
        level: 'healthy' as const,
        color: HEALTH_SCORE_CONFIG.COLORS.healthy.text,
        backgroundColor: HEALTH_SCORE_CONFIG.COLORS.healthy.background
      };
    } else if (score >= HEALTH_SCORE_CONFIG.RANGES.STABLE_MIN) {
      return {
        level: 'stable' as const,
        color: HEALTH_SCORE_CONFIG.COLORS.stable.text,
        backgroundColor: HEALTH_SCORE_CONFIG.COLORS.stable.background
      };
    } else if (score >= HEALTH_SCORE_CONFIG.RANGES.UNSTEADY_MIN) {
      return {
        level: 'unsteady' as const,
        color: HEALTH_SCORE_CONFIG.COLORS.unsteady.text,
        backgroundColor: HEALTH_SCORE_CONFIG.COLORS.unsteady.background
      };
    } else {
      return {
        level: 'critical' as const,
        color: HEALTH_SCORE_CONFIG.COLORS.critical.text,
        backgroundColor: HEALTH_SCORE_CONFIG.COLORS.critical.background
      };
    }
  }

  static extractHealthScoreValue(healthScore: unknown): number {
    if (typeof healthScore === 'number') {
      return healthScore;
    }
    if (typeof healthScore === 'object' && healthScore !== null && 'overallScore' in healthScore) {
      return (healthScore as { overallScore: number }).overallScore;
    }
    return 0;
  }

  static getHealthScoreDisplayText(level: string): string {
    return HEALTH_SCORE_CONFIG.LABELS[level as keyof typeof HEALTH_SCORE_CONFIG.LABELS] || 'Unknown';
  }

  /**
   * Get health score color by level
   */
  static getHealthScoreColor(level: string, type: 'primary' | 'background' | 'text' = 'primary'): string {
    const colors = HEALTH_SCORE_CONFIG.COLORS[level as keyof typeof HEALTH_SCORE_CONFIG.COLORS];
    return colors?.[type] || HEALTH_SCORE_CONFIG.DEFAULT_COLORS.placeholder;
  }

  /**
   * Get all health score labels as a record
   */
  static getHealthScoreLabels(): Record<string, string> {
    return HEALTH_SCORE_CONFIG.LABELS;
  }

  /**
   * Get placeholder color for empty states
   */
  static getPlaceholderColor(): string {
    return HEALTH_SCORE_CONFIG.DEFAULT_COLORS.placeholder;
  }

  static getMetricColorByValue(value: number): string {
    if (value >= HEALTH_SCORE_CONFIG.RANGES.EXCELLENT_MIN) {
      return HEALTH_SCORE_CONFIG.COLORS.excellent.primary;
    } else if (value >= HEALTH_SCORE_CONFIG.RANGES.HEALTHY_MIN) {
      return HEALTH_SCORE_CONFIG.COLORS.healthy.primary;
    } else if (value >= HEALTH_SCORE_CONFIG.RANGES.STABLE_MIN) {
      return HEALTH_SCORE_CONFIG.COLORS.stable.primary;
    } else if (value >= HEALTH_SCORE_CONFIG.RANGES.UNSTEADY_MIN) {
      return HEALTH_SCORE_CONFIG.COLORS.unsteady.primary;
    } else {
      return HEALTH_SCORE_CONFIG.COLORS.critical.primary;
    }
  }

  /**
   * Get progress bar color based on value using new progress bar ranges
   * > 75 is positive, 25 < x <= 75 is warning, <= 25 is negative
   */
  static getProgressBarColorByValue(value: number): string {
    if (value > HEALTH_SCORE_CONFIG.PROGRESS_BAR_RANGES.POSITIVE_MIN) {
      return HEALTH_SCORE_CONFIG.PROGRESS_BAR_COLORS.positive;
    } else if (value >= HEALTH_SCORE_CONFIG.PROGRESS_BAR_RANGES.WARNING_MIN) {
      return HEALTH_SCORE_CONFIG.PROGRESS_BAR_COLORS.warning;
    } else {
      return HEALTH_SCORE_CONFIG.PROGRESS_BAR_COLORS.negative;
    }
  }

  /**
   * Transform CDP health score object to UI HealthMetric format
   * Maps the detailed breakdown to the format expected by the health score popover
   * Uses new progress bar color mapping based on metric values
   */
  static transformToHealthMetrics(healthScoreData: unknown): {label: string; percent: number; value: number; bgColor: string}[] {
    if (!healthScoreData || typeof healthScoreData !== 'object') {
      return [];
    }

    const data = healthScoreData as Record<string, unknown>;
    const metrics = [];

    // Contributors metric
    if (typeof data.contributorPercentage === 'number') {
      const value = Math.round(data.contributorPercentage || 0);
      metrics.push({
        label: 'Contributors',
        percent: value,
        value: value,
        bgColor: this.getProgressBarColorByValue(value)
      });
    }

    // Popularity metric
    if (typeof data.popularityPercentage === 'number') {
      const value = Math.round(data.popularityPercentage || 0);
      metrics.push({
        label: 'Popularity',
        percent: value,
        value: value,
        bgColor: this.getProgressBarColorByValue(value)
      });
    }

    // Development metric
    if (typeof data.developmentPercentage === 'number') {
      const value = Math.round(data.developmentPercentage || 0);
      metrics.push({
        label: 'Development',
        percent: value,
        value: value,
        bgColor: this.getProgressBarColorByValue(value)
      });
    }

    // Security metric
    if (typeof data.securityPercentage === 'number') {
      const value = Math.round(data.securityPercentage || 0);
      metrics.push({
        label: 'Security',
        percent: value,
        value: value,
        bgColor: this.getProgressBarColorByValue(value)
      });
    }

    // If no detailed metrics available, return empty array
    // Component will show default/placeholder metrics
    return metrics;
  }

  static transformBackendProjectsToAutocomplete(projects: unknown[]): {id: string; name: string; logo?: string; slug?: string; description?: string; healthScore?: HealthScore}[] {
    return projects?.map((project: unknown) => {
      const proj = project as Record<string, unknown>;
      return {
        id: (proj.id as string) || '',
        name: (proj.name as string) || '',
        logo: (proj.logo as string) || '',
        slug: (proj.slug as string) || '',
        description: (proj.description as string) || '',
        healthScore: this.mapHealthScoreToLevel(this.extractHealthScoreValue(proj.healthScore))
      };
    }) || [];
  }

  static transformHealthMetricsResponse(response: {projects: unknown[]}): Record<string, unknown> {
    const healthMetricsMap: Record<string, unknown> = {};

    response.projects?.forEach((project: unknown) => {
      const proj = project as Record<string, unknown>;
      if (proj.slug && typeof proj.slug === 'string') {
        healthMetricsMap[proj.slug] = {
          healthScore: proj.healthScore,
          id: proj.id,
          name: proj.name,
          description: proj.description || ''
        };
      }
    });

    return healthMetricsMap;
  }

  static generateXAxisLabels(chartData: ChartData[]): string[] {
    return chartData.map(dataPoint => {
      const date = new Date(dataPoint.spanDate);
      return date.toLocaleDateString('en-US', {
        month: 'short',
        year: 'numeric'
      });
    });
  }

  /**
   * Process and sort chart data chronologically by spanDate
   * Ensures consistent data structure and chronological ordering
   * @param chartData - Array of chart data points to process
   * @returns Sorted array of chart data points
   */
  static processAndSortChartData(chartData: ChartData[]): ChartData[] {
    if (!chartData || chartData.length === 0) {
      return [];
    }

    const processedData = chartData.map((dataPoint: ChartData) => ({
      spanDate: dataPoint.spanDate,
      year: dataPoint.year,
      month: dataPoint.month,
      day: dataPoint.day,
      metricCount: dataPoint.metricCount,
      othersCount: dataPoint.othersCount,
      totalCount: dataPoint.totalCount,
    }));

    return processedData.sort(
      (a, b) => new Date(a.spanDate).getTime() - new Date(b.spanDate).getTime()
    );
  }

  /**
   * Format the first date from chart data for display
   * @param chartData - Array of chart data points
   * @returns Formatted first date string (e.g., "Jan 2024") or "No data" if empty
   */
  static formatFirstDate(chartData: ChartData[]): string {
    if (!chartData || chartData.length === 0) {
      return 'No data';
    }

    const sortedData = this.processAndSortChartData(chartData);
    return new Date(sortedData[0].spanDate).toLocaleDateString('en-US', {
      month: 'short',
      year: 'numeric',
    });
  }

  /**
   * Format the last date from chart data for display
   * @param chartData - Array of chart data points
   * @returns Formatted last date string (e.g., "Dec 2024") or "No data" if empty
   */
  static formatLastDate(chartData: ChartData[]): string {
    if (!chartData || chartData.length === 0) {
      return 'No data';
    }

    const sortedData = this.processAndSortChartData(chartData);
    return new Date(sortedData[sortedData.length - 1].spanDate).toLocaleDateString('en-US', {
      month: 'short',
      year: 'numeric',
    });
  }
}

/**
 * UI Text Constants for Key Projects feature
 */
export const KEY_PROJECTS_TEXT = {
  TOOLTIP: {
    DISABLED_ADD_PROJECT: {
      TITLE: 'Select workspace to add a project',
      CONTENT: 'You can only add projects in a workspace. You are viewing "All key projects" across all workspaces. Select a workspace in the drop-down on the left of the page to enable adding a project.'
    }
  }
} as const;
